/**
 * BestFareLine — which fare we picked for this traveller, and why (register P1/P2).
 *
 * One label, one line. A default answer invites them to Settings, because their
 * own details would sharpen it. Renders nothing when a saved choice is in charge.
 */
import { Link } from "react-router-dom";
import type { PlanBestFare } from "@/lib/fareOptionsTypes";
import type { PlanFlight } from "@/lib/types";

const money = (n: number) => "$" + (Number.isInteger(n) ? String(n) : n.toFixed(2));

/** "$134 each" or, with a ground cost, "$189 each, with $55 getting to DFW". */
export function bestFarePriceText(b: Pick<PlanBestFare, "total" | "groundCost" | "groundAirport">): string {
  if (b.groundCost == null) return money(b.total) + " each";
  return money(b.total) + " each, with " + money(b.groundCost) + " getting to " + (b.groundAirport ?? "the airport");
}

export default function BestFareLine({ flight }: { flight: Pick<PlanFlight, "bestFare"> | null | undefined }) {
  const best = flight?.bestFare;
  if (!best) return null;
  return (
    <div className="space-y-0.5 rounded-md border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/[0.04] px-2.5 py-2 text-xs">
      <p className="flex flex-wrap items-baseline gap-x-1.5 font-medium text-foreground">
        <span className="text-[hsl(var(--primary))]">{best.label}</span>
        <span>
          {best.fareLevel} · <span className="tabular-nums">{bestFarePriceText(best)}</span>
        </span>
      </p>
      <p className="text-muted-foreground">{best.why}</p>
      {best.groundLine && <p className="text-muted-foreground">{best.groundLine}</p>}
      {best.groundNote && <p className="text-muted-foreground">{best.groundNote}</p>}
      {best.unknownNote && <p className="text-muted-foreground">{best.unknownNote}</p>}
      {best.mode === "default" && (
        <Link to="/settings" className="inline-block font-medium text-[hsl(var(--primary))] underline-offset-2 hover:underline">
          Tell us how you fly
        </Link>
      )}
    </div>
  );
}
